import * as AccountingUtils from "../AccountingUtils";
import ItemType from "../reference/ItemType";
import RevenueArrangementUtils from "./RevenueArrangementUtils";

class RevenueAllocationStrategy {
    applies(params) {
        return false;
    }
    calculate(params) {
        const { salesOrder, revenueArrangement } = params;
        throw new Error(
            `Calculate method not implemented for sales order ${salesOrder.id} and revenue arrangement ${revenueArrangement.id}`
        );
    }
}

class ZeroESPNonCreditNoteAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { arrangementContext } = params;
        const { foundNonZeroSSP, totalESP } = arrangementContext;
        return foundNonZeroSSP && totalESP == 0;
    }

    calculate(params) {
        const { revenueArrangementItem } = params;

        revenueArrangementItem.productRevenue =
            revenueArrangementItem.extendedEstimatedSalePrice;
        if (revenueArrangementItem.itemType == ItemType.SPLIT_UNRECOGNIZED) {
            revenueArrangementItem.productRevenue =
                revenueArrangementItem.unrecognizedRevenue;
        }
    }
}

class CreditNoteDefaultAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        return true;
    }

    calculate(params) {
        const {
            salesOrder,
            revenueArrangement,
            revenueArrangementItem: raItem,
            context,
            arrangementContext
        } = params;
        const { nonCreditNoteRAIByProductId } = arrangementContext;

        const {
            creditNoteRAIByReferenceLineItemId,
            nonCreditNoteRAIByReferenceLineItemId,
            creditNoteRAIByProductId,
            totalESP,
            standaloneESP,
            totalCnBooking,
            totalPoCnBooking,
            foundNonZeroSSP
        } = arrangementContext;

        let totalBookingToAllocate = getTotalBookingToAllocate(params);

        let percentOfTotalInvoiceLineItem;
        let nonCreditNoteLineRelativeSSPProportion;

        if (salesOrder.contractId) {
            let totalNCExtendedSalePrice = 0;
            let totalNCExtendedEstimatedSalePrice = 0;
            for (let ncrai of nonCreditNoteRAIByProductId[
                raItem.salesOrderItem.productId
            ]) {
                totalNCExtendedSalePrice =
                    totalNCExtendedSalePrice + ncrai.extendedSalePrice;
                totalNCExtendedEstimatedSalePrice =
                    totalNCExtendedEstimatedSalePrice +
                    ncrai.extendedEstimatedSalePrice;
            }

            percentOfTotalInvoiceLineItem =
                raItem.extendedSalePrice /
                (raItem.extendedSalePrice + totalNCExtendedSalePrice);
            nonCreditNoteLineRelativeSSPProportion =
                totalNCExtendedEstimatedSalePrice / totalESP || 0;
        } else {
            const nonCreditNoteRAI =
                nonCreditNoteRAIByReferenceLineItemId[
                    raItem.salesOrderItem.referenceLineItemId
                ];
            percentOfTotalInvoiceLineItem =
                raItem.extendedSalePrice /
                (raItem.extendedSalePrice + nonCreditNoteRAI.extendedSalePrice);

            nonCreditNoteLineRelativeSSPProportion =
                nonCreditNoteRAI.extendedEstimatedSalePrice / totalESP || 0;
        }

        if (
            isNaN(percentOfTotalInvoiceLineItem) ||
            !isFinite(percentOfTotalInvoiceLineItem)
        ) {
            percentOfTotalInvoiceLineItem = -1;
        }

        // non credit note line percentage
        raItem.percentOfTotalInvoiceLineItem = percentOfTotalInvoiceLineItem;

        raItem.productRevenue =
            nonCreditNoteLineRelativeSSPProportion *
            percentOfTotalInvoiceLineItem *
            totalBookingToAllocate;

        // If totalSSP nets to Zero but the items are non zero
        if (
            raItem.ssp.standalonePrice &&
            raItem.ssp.standalonePrice.type == "AS_IS" &&
            foundNonZeroSSP &&
            totalESP != 0 &&
            raItem.productRevenue == 0
        ) {
            raItem.productRevenue = raItem.extendedEstimatedSalePrice || 0;
        }
    }
}

class ParentCreditNoteAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { revenueArrangementItem } = params;
        return revenueArrangementItem.salesOrderItem.parentSalesOrderItemId;
    }

    calculate(params) {
        const { revenueArrangementItem, context, arrangementContext } = params;
        const { poTotalESPByParentSalesOrderItemId, raiBySalesOrderItemId } =
            arrangementContext;
        const parentSalesOrderItemId =
            revenueArrangementItem.salesOrderItem.parentSalesOrderItemId;
        revenueArrangementItem.percentOfTotalInvoiceLineItem = 0;
        const parentItem = raiBySalesOrderItemId[parentSalesOrderItemId];
        const cnRelativeSSPProportion =
            revenueArrangementItem.extendedEstimatedSalePrice /
            poTotalESPByParentSalesOrderItemId[parentSalesOrderItemId];
        revenueArrangementItem.productRevenue =
            parentItem.extendedSalePrice * cnRelativeSSPProportion;
    }
}

class ReasonCodeAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { revenueArrangementItem, context, arrangementContext } = params;
        const { orgConfig } = context;
        const { foundNonZeroSSP } = arrangementContext;
        return (
            !foundNonZeroSSP ||
            (AccountingUtils.itemHasReasonCode(
                revenueArrangementItem,
                orgConfig
            ) &&
                !revenueArrangementItem.salesOrderItem.poProcessed &&
                !["SIMPLE_PERCENT_NET_RANGE", "PERCENT_NET_RANGE"].includes(
                    revenueArrangementItem.ssp.standalonePrice.type
                ))
        );
    }

    calculate(params) {
        const { revenueArrangementItem } = params;
        // any item that has a reason code and
        // this item is not such that it has child products (PO products) see SalesOrderPreprocessor
        // and this item's ssp type is not one of percentage range.
        // then the product revenue is always extendedSalePrice.
        // please see ticket: REV-8406
        revenueArrangementItem.productRevenue =
            revenueArrangementItem.extendedSalePrice;
    }
}

class StandaloneAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { revenueArrangementItem } = params;
        return (
            revenueArrangementItem.ssp.standalonePrice &&
            revenueArrangementItem.ssp.standalonePrice.type == "STANDALONE"
        );
    }

    calculate(params) {
        const { revenueArrangementItem } = params;
        // We separated STANDALONE esp thinking about the significant financing component.
        // That should not have been handled with STANDALONE.
        if (revenueArrangementItem.itemType == ItemType.SPLIT_UNRECOGNIZED) {
            revenueArrangementItem.productRevenue =
                revenueArrangementItem.unrecognizedRevenue;
        } else {
            revenueArrangementItem.productRevenue =
                revenueArrangementItem.extendedEstimatedSalePrice;
        }
    }
}

class StandaloneCnAllocationStrategy extends StandaloneAllocationStrategy {
    calculate(params) {
        const { revenueArrangementItem } = params;
        // We separated STANDALONE esp thinking about the significant financing component.
        // That should not have been handled with STANDALONE.
        revenueArrangementItem.percentOfTotalInvoiceLineItem = 0;
        if (revenueArrangementItem.itemType == ItemType.SPLIT_UNRECOGNIZED) {
            revenueArrangementItem.productRevenue =
                revenueArrangementItem.unrecognizedRevenue;
        } else {
            revenueArrangementItem.productRevenue =
                revenueArrangementItem.extendedEstimatedSalePrice;
        }
    }
}

class WithCnAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { salesOrder, arrangementContext, revenueArrangementItem } =
            params;
        const {
            creditNoteRevenueArrangementItem,
            creditNoteRAIByReferenceLineItemId
        } = arrangementContext;
        return (
            !salesOrder.contractId &&
            creditNoteRevenueArrangementItem.length &&
            creditNoteRAIByReferenceLineItemId[
                revenueArrangementItem.salesOrderItem.rootId ||
                    revenueArrangementItem.salesOrderItem.id
            ] &&
            creditNoteRAIByReferenceLineItemId[
                revenueArrangementItem.salesOrderItem.rootId ||
                    revenueArrangementItem.salesOrderItem.id
            ].length
        );
    }

    calculate(params) {
        const { revenueArrangementItem, arrangementContext } = params;
        const {
            totalESP,
            foundNonZeroSSP,
            creditNoteRAIByReferenceLineItemId
        } = arrangementContext;

        let totalBookingToAllocate = getTotalBookingToAllocate(params);

        // we have credit notes
        // the way we calculate product revenue is different from when we don't have credit notes.
        let creditNoteRais =
            creditNoteRAIByReferenceLineItemId[
                revenueArrangementItem.salesOrderItem.rootId ||
                    revenueArrangementItem.salesOrderItem.id
            ];
        RevenueArrangementUtils.calculateProductRevenueByCreditNote(
            creditNoteRais,
            revenueArrangementItem,
            totalESP,
            totalBookingToAllocate,
            foundNonZeroSSP
        );
    }
}

class WithCnContractTermAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { salesOrder, arrangementContext, revenueArrangementItem } =
            params;
        const { creditNoteRevenueArrangementItem, creditNoteRAIByProductId } =
            arrangementContext;
        return (
            salesOrder.contractId &&
            creditNoteRevenueArrangementItem.length &&
            creditNoteRAIByProductId[
                revenueArrangementItem.salesOrderItem.productId
            ] &&
            creditNoteRAIByProductId[
                revenueArrangementItem.salesOrderItem.productId
            ].length
        );
    }

    calculate(params) {
        const { revenueArrangementItem, arrangementContext } = params;
        const { totalESP, foundNonZeroSSP, creditNoteRAIByProductId } =
            arrangementContext;

        let totalBookingToAllocate = getTotalBookingToAllocate(params);

        // we have credit notes
        // the way we calculate product revenue is different from when we don't have credit notes.
        let creditNoteRais =
            creditNoteRAIByProductId[
                revenueArrangementItem.salesOrderItem.productId
            ];
        RevenueArrangementUtils.calculateProductRevenueByCreditNote(
            creditNoteRais,
            revenueArrangementItem,
            totalESP,
            totalBookingToAllocate,
            foundNonZeroSSP
        );
    }
}

class DefaultAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        return true;
    }

    calculate(params) {
        const {
            revenueArrangementItem,
            arrangementContext,
            revenueArrangement
        } = params;
        const { totalESP, standaloneESP, totalCnBooking } = arrangementContext;

        let totalBookingToAllocate = getTotalBookingToAllocate(params);

        // non credit note calculations
        let relativeSSPProportion =
            revenueArrangementItem.extendedEstimatedSalePrice / totalESP || 0;
        revenueArrangementItem.productRevenue =
            relativeSSPProportion * totalBookingToAllocate;
    }
}

class TransactionDateDiscountAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { revenueArrangementItem, context, arrangementContext } = params;
        const { recognizeByTransactionDate } = context;
        const { noOfDiscountItems } = arrangementContext;

        return (
            recognizeByTransactionDate &&
            noOfDiscountItems > 1 &&
            revenueArrangementItem.estimatedSalePrice
        );
    }

    calculate(params) {
        const { arrangementContext, revenueArrangementItem, context } = params;
        const { totalBookingByTransactionDate, totalEspByTransactionDate } =
            arrangementContext;

        let transactionDate =
            revenueArrangementItem.salesOrderItem.transactionDate;
        let totalBooking = totalBookingByTransactionDate[transactionDate] || 0;
        let totalEsp = totalEspByTransactionDate[transactionDate] || 0;
        revenueArrangementItem.productRevenue =
            (revenueArrangementItem.extendedEstimatedSalePrice / totalEsp) *
            totalBooking;
    }
}

class CreditNoteBacklogAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { revenueArrangementItem, context, revenueArrangement } = params;
        const { cnAllocationByBacklog } = context;

        return revenueArrangement.isTerminated && cnAllocationByBacklog;
    }

    calculate(params) {
        const { revenueArrangementItem, context } = params;

        // when this config is enabled, terminated credit notes will be processed separately
        revenueArrangementItem.percentOfTotalInvoiceLineItem = 0;
        return;
    }
}

class SkipAllocationStrategy extends RevenueAllocationStrategy {
    applies(params) {
        const { revenueArrangementItem } = params;
        return ItemType.isFrozen(revenueArrangementItem.itemType);
    }

    calculate(params) {
        // For frozen items, we do not calculate product revenue
        return;
    }
}

const sortStrategiesByPriority = (strategies, strategyPriorityMap) => {
    return strategies.sort((a, b) => {
        const priorityA = strategyPriorityMap.get(a.constructor);
        const priorityB = strategyPriorityMap.get(b.constructor);
        return priorityA - priorityB;
    });
};

const validateStrategyPriority = (strategies, strategyPriorityMap) => {
    for (const strategy of strategies) {
        const priority = strategyPriorityMap.get(strategy.constructor);
        if (priority === undefined) {
            throw new Error(
                `Missing priority for strategy: ${strategy.constructor.name}`
            );
        }
    }
};

export class CreditNoteStrategies {
    static #instance = null;

    static #STRATEGY_PRIORITY = new Map([
        [SkipAllocationStrategy, 10],
        [CreditNoteBacklogAllocationStrategy, 20],
        [TransactionDateDiscountAllocationStrategy, 30],
        [ParentCreditNoteAllocationStrategy, 40],
        [ReasonCodeAllocationStrategy, 50],
        [StandaloneCnAllocationStrategy, 60],
        [CreditNoteDefaultAllocationStrategy, 100]
    ]);

    constructor() {
        if (CreditNoteStrategies.#instance) {
            return CreditNoteStrategies.#instance;
        }

        console.log("Initializing CreditNoteStrategies singleton instance");

        const strategies = [
            new SkipAllocationStrategy(),
            new CreditNoteBacklogAllocationStrategy(),
            new TransactionDateDiscountAllocationStrategy(),
            new ParentCreditNoteAllocationStrategy(),
            new ReasonCodeAllocationStrategy(),
            new StandaloneCnAllocationStrategy(),
            new CreditNoteDefaultAllocationStrategy()
        ];

        validateStrategyPriority(
            strategies,
            CreditNoteStrategies.#STRATEGY_PRIORITY
        );
        this.strategies = sortStrategiesByPriority(
            strategies,
            CreditNoteStrategies.#STRATEGY_PRIORITY
        );

        this.selectStrategy = (params) => {
            for (const strategy of this.strategies) {
                if (strategy.applies(params)) {
                    return strategy;
                }
            }
            throw new Error("No applicable strategy found");
        };
    }

    static getInstance() {
        if (!CreditNoteStrategies.#instance) {
            CreditNoteStrategies.#instance = new CreditNoteStrategies();
        }
        return CreditNoteStrategies.#instance;
    }
}

export class NonCreditNoteStrategies {
    static #instance = null;

    static #STRATEGY_PRIORITY = new Map([
        [SkipAllocationStrategy, 10],
        [TransactionDateDiscountAllocationStrategy, 20],
        [ZeroESPNonCreditNoteAllocationStrategy, 30],
        [ReasonCodeAllocationStrategy, 40],
        [StandaloneAllocationStrategy, 50],
        [WithCnAllocationStrategy, 60],
        [WithCnContractTermAllocationStrategy, 70],
        [DefaultAllocationStrategy, 100]
    ]);

    constructor() {
        if (NonCreditNoteStrategies.#instance) {
            return NonCreditNoteStrategies.#instance;
        }

        console.log("Initializing NonCreditNoteStrategies singleton instance");

        const strategies = [
            new SkipAllocationStrategy(),
            new TransactionDateDiscountAllocationStrategy(),
            new ZeroESPNonCreditNoteAllocationStrategy(),
            new ReasonCodeAllocationStrategy(),
            new StandaloneAllocationStrategy(),
            new WithCnAllocationStrategy(),
            new WithCnContractTermAllocationStrategy(),
            new DefaultAllocationStrategy()
        ];

        validateStrategyPriority(
            strategies,
            NonCreditNoteStrategies.#STRATEGY_PRIORITY
        );
        this.strategies = sortStrategiesByPriority(
            strategies,
            NonCreditNoteStrategies.#STRATEGY_PRIORITY
        );

        this.selectStrategy = (params) => {
            for (const strategy of this.strategies) {
                if (strategy.applies(params)) {
                    return strategy;
                }
            }
            throw new Error("No applicable strategy found");
        };
    }

    static getInstance() {
        if (!NonCreditNoteStrategies.#instance) {
            NonCreditNoteStrategies.#instance = new NonCreditNoteStrategies();
        }
        return NonCreditNoteStrategies.#instance;
    }
}

function getTotalBookingToAllocate(params) {
    const {
        revenueArrangementItem,
        arrangementContext,
        revenueArrangement,
        context
    } = params;
    const { cnAllocationByBacklog } = context;
    const { standaloneESP, totalCnBooking, totalPoCnBooking } =
        arrangementContext;

    let totalBookingToAllocate =
        revenueArrangementItem.totalBooking - standaloneESP;

    if (revenueArrangement.isTerminated && cnAllocationByBacklog) {
        totalBookingToAllocate -= totalCnBooking;
    } else {
        totalBookingToAllocate -= totalPoCnBooking;
    }
    return totalBookingToAllocate;
}
